import { Alert, Button, Col, Divider, Form, Input, Layout, Row } from "antd";
import { motion } from "framer-motion";
import {
  AntCloudOutlined,
  ControlOutlined,
  InstagramOutlined,
  LockOutlined,
  ThunderboltOutlined,
  TwitterOutlined,
  UserOutlined,
  WifiOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import LogOlimpica from "../../assets/images/logo-olimpica.svg";
import { useTranslation } from "react-i18next";
import { useApiAuth } from "../../context/api-auth-context";

const FEATURES = (t) => [
  {
    icon: <ControlOutlined />,
    color: "#E30613",
    bg: "rgba(227,6,19,0.1)",
    title: t("singlePage.characteristicsOne"),
    text: t("singlePage.subCharacteristicsOne"),
  },
  {
    icon: <AntCloudOutlined />,
    color: "#1677ff",
    bg: "rgba(22,119,255,0.1)",
    title: t("singlePage.characteristicsTwo"),
    text: t("singlePage.subCharacteristicsTwo"),
  },
  {
    icon: <ThunderboltOutlined />,
    color: "#faad14",
    bg: "rgba(250,173,20,0.1)",
    title: t("singlePage.characteristicsThree"),
    text: t("singlePage.subCharacteristicsThree"),
  },
  {
    icon: <WifiOutlined />,
    color: "#52c41a",
    bg: "rgba(82,196,26,0.1)",
    title: t("singlePage.characteristicsFour"),
    text: t("singlePage.subCharacteristicsFour"),
  },
];

export default function SinglePage() {
  const apiAuth = useApiAuth();
  const { t } = useTranslation("global");
  const features = FEATURES(t);
  const [form] = Form.useForm();

  const handleLogin = async ({ username, password }) => {
    apiAuth.setError(null);
    try {
      await apiAuth.login(username, password);
    } catch {
      // error ya está en apiAuth.error
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#fff" }}>

      {/* ── Navbar ─────────────────────────────────────────── */}
      <header className="sp-navbar">
        <div className="sp-navbar__brand">
          <img src={LogOlimpica} alt="Olímpica IoT" className="sp-navbar__logo" />
        </div>
        <nav className="sp-navbar__links">
          <a href="#features" className="sp-navbar__link">
            {t("singlePage.characteristics")}
          </a>
        </nav>
        <div className="sp-navbar__actions">
          <a href="#" className="sp-navbar__link sp-navbar__link--action">
            {t("singlePage.signOn")}
          </a>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────── */}
      <section className="sp-hero" id="home">
        <div className="sp-hero__inner">

          {/* Left: value proposition */}
          <motion.div
            className="sp-hero__left"
            initial={{ opacity: 0, x: -28 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="sp-hero__badge">IoT · Tiempo Real · Olímpica</span>
            <h1 className="sp-hero__title">{t("singlePage.benefits")}</h1>
            <p className="sp-hero__sub">{t("singlePage.subCharacteristicsOne")}</p>
            <Button
              size="large"
              shape="round"
              style={{
                background: "transparent",
                borderColor: "rgba(255,255,255,0.25)",
                color: "rgba(255,255,255,0.8)",
                height: 48,
                padding: "0 32px",
                fontSize: 15,
                fontWeight: 600,
                marginTop: 8,
              }}
              href="#features"
            >
              {t("singlePage.characteristics")} →
            </Button>
          </motion.div>

          {/* Right: login form */}
          <motion.div
            className="sp-hero__right"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="sp-login-card">
              <div className="sp-login-card__logo-wrap">
                <img src={LogOlimpica} alt="Olímpica" className="sp-login-card__logo" />
              </div>
              <h2 className="sp-login-card__title">{t("login.title")}</h2>
              <p className="sp-login-card__sub">{t("login.subtitle")}</p>

              {apiAuth.error && (
                <Alert
                  type="error"
                  message={apiAuth.error}
                  showIcon
                  closable
                  onClose={() => apiAuth.setError(null)}
                  style={{ marginBottom: 20, borderRadius: 8 }}
                />
              )}

              <Form
                form={form}
                layout="vertical"
                onFinish={handleLogin}
                size="large"
                requiredMark={false}
              >
                <Form.Item
                  name="username"
                  rules={[{ required: true, message: t("login.usernameRequired") }]}
                >
                  <Input
                    prefix={<UserOutlined className="sp-login-input-icon" />}
                    placeholder={t("login.usernamePlaceholder")}
                    className="sp-login-input"
                    autoComplete="username"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[{ required: true, message: t("login.passwordRequired") }]}
                >
                  <Input.Password
                    prefix={<LockOutlined className="sp-login-input-icon" />}
                    placeholder={t("login.passwordPlaceholder")}
                    className="sp-login-input"
                    autoComplete="current-password"
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={apiAuth.isLoading}
                    style={{
                      background: "#E30613",
                      borderColor: "#E30613",
                      height: 46,
                      fontWeight: 700,
                      fontSize: 15,
                      borderRadius: 8,
                      boxShadow: "0 4px 16px rgba(227,6,19,0.4)",
                    }}
                  >
                    {t("login.submit")}
                  </Button>
                </Form.Item>
              </Form>
              <p className="sp-login-card__help">{t("login.help")}</p>
            </div>
          </motion.div>

        </div>
      </section>

      {/* ── Features ───────────────────────────────────────── */}
      <section className="sp-features" id="features">
        <div className="sp-features__inner">
          <motion.h2
            className="sp-features__title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {t("singlePage.characteristics")}
          </motion.h2>
          <Row gutter={[24, 24]}>
            {features.map((feat, i) => (
              <Col xs={24} sm={12} md={12} lg={6} key={i}>
                <motion.div
                  className="sp-feature-card"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                >
                  <div
                    className="sp-feature-card__icon"
                    style={{ background: feat.bg, color: feat.color }}
                  >
                    {feat.icon}
                  </div>
                  <h3 className="sp-feature-card__title">{feat.title}</h3>
                  <p className="sp-feature-card__text">{feat.text}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────── */}
      <footer className="sp-footer">
        <div className="sp-footer__brand">
          <img src={LogOlimpica} alt="Olímpica" className="sp-footer__logo" />
          <span className="sp-footer__copy">{t("singlePage.copyright", { year: new Date().getFullYear() })}</span>
        </div>
        <div className="sp-footer__links">
          <a href="#home" className="sp-footer__link">{t("singlePage.about")}</a>
          <a href="#home" className="sp-footer__link">{t("singlePage.contact")}</a>
          <a href="#home" className="sp-footer__link">{t("singlePage.help")}</a>
        </div>
        <div className="sp-footer__social">
          <InstagramOutlined className="sp-footer__icon" />
          <TwitterOutlined  className="sp-footer__icon" />
          <YoutubeOutlined  className="sp-footer__icon" />
        </div>
      </footer>

    </Layout>
  );
}
